import {
    CostPriceIngredient,
    EnrichedCostPriceAnalysis,
    IngredientAlternative,
} from "../../../../services/costPrice/costPriceService";
import { Subrecipe } from "../../../../services/subrecipes/subrecipesService";
import { CustomIngredient } from "../../../../services/customIngredients/customIngredientsService";
import { OrderHistory } from "../../../../stores/appOrderHistoryStore";
import {
    applyByoQuantityOverrides,
    COST_PRICE_TYPE_CHOICE,
} from "../../../../utils/costPriceUtils";
import { applyWaste, resolveWaste } from "../../../../utils/wasteUtils";
import { getPortionsUnitDisplay } from "../../../../utils/ingredientGrouping";
import { DishRowData } from "./costPriceDataTypes";
import { buildDishCells, escapeCsvCell } from "./costPriceExport";
import {
    indent,
    money,
    quantity,
    SubrecipeLine,
    supplierOf,
    unitPrice,
    walkSubrecipeLines,
} from "./subrecipesExport";

/**
 * Detailed cost price export: the same dish rows as the plain export, but each
 * one followed by its recipe underneath — one line per ingredient, subrecipe,
 * nested menu item, BYO add-on and alternative.
 *
 * How to read the sheet:
 *   - `Type` is authoritative; the indentation in `Name` is only for humans.
 *   - Every non-dish line reads the same way: `Cost = Quantity × (1 + Waste%) ×
 *     Unit price`, where `Unit price` is the price of ONE `Unit` (one g / ml /
 *     piece, one portion unit of a subrecipe, one pick of an add-on).
 *   - Subrecipe and nested-menu-item lines are followed by their own contents,
 *     with quantities already SCALED to the parent dish — so those child lines
 *     sum to the container's `Cost`, and "how much do I need for one dish" needs
 *     no further math.
 *   - BYO add-on lines are the exception: their contents are listed per ONE PICK
 *     (that IS the recipe of the add-on), so they sum to the add-on's `Unit
 *     price`, while the add-on's own `Cost` is its weighted contribution to the
 *     dish.
 *   - Alternatives carry quantity + waste but deliberately NO price or cost:
 *     cost math ignores them (they only feed the waste-allocation engine), so a
 *     cost there would double-count the `Cost` column.
 *
 * Every number is derived from the SAME enriched analyses the table renders, so
 * the dish lines match the plain export exactly.
 */

const COLUMNS = 15;

// Where the nine dish-level cells from `buildDishCells` land in the wider
// layout: name, menu item, category, matrix, cost, price, cost %, margin, sold.
const DISH_CELL_POSITIONS = [1, 2, 3, 4, 10, 11, 12, 13, 14];

// Detail-line columns.
const COL_TYPE = 0;
const COL_NAME = 1;
const COL_SUPPLIER = 5;
const COL_QUANTITY = 6;
const COL_UNIT = 7;
const COL_WASTE = 8;
const COL_UNIT_PRICE = 9;
const COL_COST = 10;

/** Nested cost-price recursion budget — mirrors `MAX_DEPTH` in `enrichCostPriceRecursive`. */
const NESTED_MAX_DEPTH = 3;

/** Row-type labels + the orphan fallback, passed in translated by the caller. */
export interface DetailedExportLabels {
    dish: string;
    ingredient: string;
    alternative: string;
    subrecipe: string;
    menuItem: string;
    addOn: string;
    /** Unit on an add-on line: picks of that choice per dish sold. */
    picksUnit: string;
    deletedSubrecipe: string;
}

/**
 * Everything the walk needs to turn a reference into a live number. Mirrors the
 * inputs `enrichCostPriceListWithCurrentPrices` was called with, so the costs
 * printed here reproduce the ones on screen.
 */
export interface DetailedExportContext {
    orderHistory: OrderHistory[];
    customIngredients?: CustomIngredient[];
    subrecipesById: Map<string, Subrecipe>;
    /** The enriched list — nested cost prices and CHOICEs resolve against it. */
    allAnalyses: EnrichedCostPriceAnalysis[];
    showExVat: boolean;
    defaultWastePercentage: number;
    /** Cross-restaurant sourcing data, keyed by source restaurant id. */
    byRestaurant?: {
        orders: Map<string, OrderHistory[]>;
        custom: Map<string, CustomIngredient[]>;
        subrecipes: Map<string, Map<string, Subrecipe>>;
    };
    currentRestaurantId?: string;
    labels: DetailedExportLabels;
}

/** The data set an ingredient must be priced against (current or sibling restaurant). */
interface RestaurantData {
    orders: OrderHistory[];
    custom?: CustomIngredient[];
    subrecipes: Map<string, Subrecipe>;
}

type Cells = string[];

// Cell formatters (money / unitPrice / quantity / indent / supplierOf) live in
// `subrecipesExport.ts`, shared with the subrecipe exports.

const emptyRow = (): Cells => new Array(COLUMNS).fill('');

/** One dish line — the plain export's nine cells, spread into the wide layout. */
function dishRow(row: DishRowData, labels: DetailedExportLabels): Cells {
    const cells = emptyRow();
    cells[COL_TYPE] = labels.dish;
    buildDishCells(row).forEach((value, i) => {
        cells[DISH_CELL_POSITIONS[i]] = String(value ?? '');
    });
    return cells;
}

/** One recipe line (ingredient / alternative / subrecipe / nested item / add-on). */
function detailRow(args: {
    type: string;
    name: string;
    level: number;
    supplier?: string;
    qty?: number;
    unit?: string;
    waste?: number;
    price?: number;
    cost?: number;
}): Cells {
    const cells = emptyRow();
    cells[COL_TYPE] = args.type;
    cells[COL_NAME] = indent(args.level, args.name);
    cells[COL_SUPPLIER] = args.supplier ?? '';
    cells[COL_QUANTITY] = quantity(args.qty);
    cells[COL_UNIT] = args.unit ?? '';
    cells[COL_WASTE] = quantity(args.waste);
    cells[COL_UNIT_PRICE] = unitPrice(args.price);
    cells[COL_COST] = money(args.cost);
    return cells;
}

// --- the walk ----------------------------------------------------------------

/**
 * Resolve which restaurant's data prices this row. A sibling-sourced row draws
 * from `byRestaurant`; everything else from the current restaurant. Missing
 * sibling data degrades to empty sets → no price → blank cells, never a crash.
 * Mirrors `resolveData` in `enrichCostPriceRecursive`.
 */
function resolveData(ctx: DetailedExportContext, sourceRestaurantId?: string): RestaurantData {
    if (sourceRestaurantId && ctx.byRestaurant && sourceRestaurantId !== ctx.currentRestaurantId) {
        return {
            orders: ctx.byRestaurant.orders.get(sourceRestaurantId) ?? [],
            custom: ctx.byRestaurant.custom.get(sourceRestaurantId) ?? [],
            subrecipes: ctx.byRestaurant.subrecipes.get(sourceRestaurantId) ?? new Map<string, Subrecipe>(),
        };
    }
    return { orders: ctx.orderHistory, custom: ctx.customIngredients, subrecipes: ctx.subrecipesById };
}

/** Alternatives of one ingredient row — quantity + waste only (never priced). */
function emitAlternatives(
    alternatives: IngredientAlternative[] | undefined,
    scale: number,
    level: number,
    ctx: DetailedExportContext,
    out: Cells[]
): void {
    for (const alt of alternatives ?? []) {
        out.push(detailRow({
            type: ctx.labels.alternative,
            name: alt.productName,
            level,
            supplier: supplierOf(alt.platform, alt.customPlatformName),
            qty: (alt.quantityNeeded ?? 0) * scale,
            unit: alt.quantityInfo?.stdUnit ?? '',
            waste: resolveWaste(alt.wastePercentage, ctx.defaultWastePercentage),
        }));
    }
}

/**
 * A plain ingredient row of a cost-price analysis. The price is already on the
 * row (the analyses handed in are enriched), so nothing is recomputed here.
 */
function emitCostPriceIngredient(
    ing: CostPriceIngredient,
    scale: number,
    level: number,
    ctx: DetailedExportContext,
    out: Cells[]
): void {
    const qty = (ing.quantityNeeded ?? 0) * scale;
    const waste = resolveWaste(ing.wastePercentage, ctx.defaultWastePercentage);
    const price = ing.currentPrice;
    out.push(detailRow({
        type: ctx.labels.ingredient,
        name: ing.productName ?? '',
        level,
        supplier: supplierOf(ing.platform, ing.customPlatformName),
        qty,
        unit: ing.quantityInfo?.stdUnit ?? '',
        waste,
        price,
        cost: price === undefined ? undefined : applyWaste(qty, waste) * price,
    }));
    emitAlternatives(ing.alternatives, scale, level + 1, ctx, out);
}

/**
 * The ingredients INSIDE a subrecipe, scaled to the parent dish. The walk (and
 * the live price lookup that makes the lines sum to the subrecipe's unit cost)
 * is shared with the subrecipe exports — this only maps its lines into cells.
 */
function emitSubrecipeIngredients(
    subrecipe: Subrecipe,
    scale: number,
    level: number,
    data: RestaurantData,
    ctx: DetailedExportContext,
    out: Cells[],
    depth: number,
    visited: Set<string>
): void {
    const lines: SubrecipeLine[] = [];
    walkSubrecipeLines(
        subrecipe, scale, 0, data, ctx.showExVat, ctx.defaultWastePercentage, lines, depth, visited
    );
    const typeLabels: Record<SubrecipeLine['type'], string> = {
        ingredient: ctx.labels.ingredient,
        subrecipe: ctx.labels.subrecipe,
        alternative: ctx.labels.alternative,
    };
    for (const line of lines) {
        out.push(detailRow({
            type: typeLabels[line.type],
            name: line.deleted ? ctx.labels.deletedSubrecipe : line.name,
            level: level + line.level,
            supplier: line.supplier,
            qty: line.qty,
            unit: line.unit,
            waste: line.waste,
            price: line.price,
            cost: line.cost,
        }));
    }
}

/**
 * One analysis' recipe: subrecipes, nested menu items, then plain ingredients —
 * the order the dish detail panel uses. `scale` multiplies every quantity (a
 * nested menu item used 2× lists its ingredients at 2× the quantity).
 */
function emitAnalysisRecipe(
    analysis: { id?: string; ingredients: CostPriceIngredient[] },
    scale: number,
    level: number,
    ctx: DetailedExportContext,
    out: Cells[],
    depth: number,
    visitedCostPriceIds: Set<string>
): void {
    const isSubrecipeRef = (ing: CostPriceIngredient) => !!ing.subrecipeId && !ing.articleId;

    for (const ref of analysis.ingredients.filter(isSubrecipeRef)) {
        const data = resolveData(ctx, ref.sourceRestaurantId);
        const subrecipe = data.subrecipes.get(ref.subrecipeId!);
        const portionsNeeded = (ref.subrecipePortionsNeeded ?? 0) * scale;
        // Enriched ref rows carry the subrecipe's cost per portion unit.
        const perPortion = ref.currentPrice;

        out.push(detailRow({
            type: ctx.labels.subrecipe,
            name: subrecipe?.name ?? ctx.labels.deletedSubrecipe,
            level,
            qty: portionsNeeded,
            unit: subrecipe ? getPortionsUnitDisplay(subrecipe.portionsUnit) : '',
            price: perPortion,
            cost: perPortion === undefined ? undefined : perPortion * portionsNeeded,
        }));

        if (subrecipe && subrecipe.portions) {
            emitSubrecipeIngredients(
                subrecipe, portionsNeeded / subrecipe.portions, level + 1, data, ctx, out, 0,
                new Set([subrecipe.id])
            );
        }
    }

    for (const ref of analysis.ingredients.filter(ing => !!ing.nestedCostPriceId)) {
        // `|| 1` mirrors the cost math in `enrichCostPriceRecursive`, so this line
        // reconciles with the parent's total.
        const perDish = ref.nestedQuantityNeeded || 1;
        const qty = perDish * scale;
        const nested = ctx.allAnalyses.find(a => a.id === ref.nestedCostPriceId);

        out.push(detailRow({
            type: ctx.labels.menuItem,
            name: ref.nestedCostPriceName ?? nested?.costPriceName ?? '',
            level,
            qty,
            price: ref.currentPrice,
            cost: ref.currentPrice === undefined ? undefined : ref.currentPrice * qty,
        }));

        if (nested && depth < NESTED_MAX_DEPTH && !visitedCostPriceIds.has(nested.id)) {
            const nextVisited = new Set(visitedCostPriceIds);
            nextVisited.add(nested.id);
            emitAnalysisRecipe(nested, qty, level + 1, ctx, out, depth + 1, nextVisited);
        }
    }

    for (const ing of analysis.ingredients) {
        if (isSubrecipeRef(ing) || ing.nestedCostPriceId) continue;
        emitCostPriceIngredient(ing, scale, level, ctx, out);
    }
}

/**
 * BYO (build-your-own) add-ons. A BYO dish's cost is the weighted average over
 * the choices its guests actually picked, so its own ingredients alone don't add
 * up to the dish `Cost`. Each add-on is listed like a subrecipe: quantity =
 * average picks per dish sold, unit price = cost of one pick, cost = the two
 * multiplied — which is exactly that choice's contribution to the weighted dish
 * cost. Its ingredients follow, priced per ONE pick.
 */
function emitByoAddOns(
    analysis: EnrichedCostPriceAnalysis,
    level: number,
    ctx: DetailedExportContext,
    out: Cells[]
): void {
    const byo = analysis.byo;
    if (!byo || byo.baseQty <= 0) return;

    for (const choice of byo.choices) {
        const picksPerDish = choice.qtySold / byo.baseQty;
        out.push(detailRow({
            type: ctx.labels.addOn,
            name: choice.name,
            level,
            qty: picksPerDish,
            unit: ctx.labels.picksUnit,
            price: choice.perPickCost,
            cost: choice.perPickCost === undefined ? undefined : choice.perPickCost * picksPerDish,
        }));

        // The shared CHOICE analysis, with this dish's per-dish quantity
        // overrides applied — the same rows the enrichment costed.
        const choiceAnalysis = ctx.allAnalyses.find(
            a => a.costPriceType === COST_PRICE_TYPE_CHOICE
                && a.restaurantId === analysis.restaurantId
                && a.costPriceName === choice.name
        );
        if (!choiceAnalysis) continue;

        const overrides = analysis.byoChoiceOverrides?.[choice.name];
        emitAnalysisRecipe(
            { id: choiceAnalysis.id, ingredients: applyByoQuantityOverrides(choiceAnalysis.ingredients, overrides) },
            1,
            level + 1,
            ctx,
            out,
            0,
            new Set([analysis.id, choiceAnalysis.id])
        );
    }
}

// --- entry point -------------------------------------------------------------

/**
 * Build the detailed CSV for exactly the rows the table is showing (all filters,
 * search and sorting already applied) — the export never re-derives or
 * re-filters data.
 */
export function buildCostPriceDetailedCsv(
    rows: DishRowData[],
    headers: string[],
    ctx: DetailedExportContext
): string {
    const analysisById = new Map(ctx.allAnalyses.map(a => [a.id, a]));
    const lines = [headers.map(escapeCsvCell).join(',')];

    for (const row of rows) {
        const cells: Cells[] = [dishRow(row, ctx.labels)];
        // Placeholders (revenue items with no analysis yet) have no recipe.
        if (!row.isPlaceholder) {
            emitAnalysisRecipe(row, 1, 1, ctx, cells, 0, new Set([row.id]));
            const enriched = analysisById.get(row.id);
            if (enriched) emitByoAddOns(enriched, 1, ctx, cells);
        }
        for (const cell of cells) {
            lines.push(cell.map(escapeCsvCell).join(','));
        }
    }

    return lines.join('\n');
}
